import { type ReactElement } from "react";
import { User } from "lucide-react";
import type {
  ContainmentAction,
  ContainmentCardsParams,
  ContainmentStatus,
} from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * Containment Cards — Oppress, multi. Vertical action list with status
 * pill + progress bar per item.
 * Ported from ui-elements/components/solves/workspace/oppress/ContainmentCardsModule.tsx.
 */
const STATUS_TONE: Record<ContainmentStatus, string> = {
  Done: "bg-pb-green/15 text-pb-green-text border-pb-green/40",
  "In Progress": "bg-pb-cyan-soft text-pb-cyan-text border-pb-cyan/40",
  "At Risk": "bg-pb-amber-soft text-pb-amber-text border-pb-amber/40",
  Blocked: "bg-pb-red/15 text-pb-red-text border-pb-red/40",
};

const PROGRESS_TONE: Record<ContainmentStatus, string> = {
  Done: "bg-pb-green",
  "In Progress": "bg-pb-cyan",
  "At Risk": "bg-pb-amber",
  Blocked: "bg-pb-red",
};

export function ContainmentCardsWidget({
  data,
}: WidgetBodyProps<ContainmentCardsParams>): ReactElement {
  if (data.actions.length === 0) {
    return (
      <div className="text-pb-text-muted text-xs italic py-3 flex-1 grid place-items-center text-center px-4">
        No containment actions yet — ask PlayerAI to draft a starter list.
      </div>
    );
  }
  return (
    <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
      <div className="flex flex-col gap-2.5">
        {data.actions.map((a) => (
          <Item key={a.id} action={a} />
        ))}
      </div>
    </div>
  );
}

function Item({ action }: { action: ContainmentAction }): ReactElement {
  return (
    <div className="rounded-md border border-pb-border bg-pb-card-elevated/60 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="text-pb-text font-medium text-xs leading-snug">
          {action.title}
        </div>
        <span
          className={cn(
            "shrink-0 text-dense-2 uppercase tracking-wider font-semibold py-0.5 px-1.5 rounded-md border",
            STATUS_TONE[action.status],
          )}
        >
          {action.status}
        </span>
      </div>
      {action.detail ? (
        <div className="text-xs text-pb-text-muted mt-1 leading-snug">
          {action.detail}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 mt-2.5 text-dense-1 text-pb-text-muted">
        <span className="inline-flex items-center gap-1">
          <User className="w-3 h-3" />
          <span className="text-pb-text">{action.owner}</span>
        </span>
        <span>Due {action.due}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-pb-border overflow-hidden">
          <div
            className={cn(
              "h-full transition-[width] duration-motion-layout",
              PROGRESS_TONE[action.status],
            )}
            style={{
              width: `${Math.max(0, Math.min(100, action.percentComplete))}%`,
            }}
          />
        </div>
        <span className="text-dense-1 tabular-nums text-pb-text-muted font-medium w-9 text-end">
          {action.percentComplete}%
        </span>
      </div>
    </div>
  );
}
